import { Autocomplete, Button, type Item, TagSelect, TextField } from '@ads/components-react';
import { ArrowPathIcon, FunnelIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import * as Popover from '@radix-ui/react-popover';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DatePicker } from '@/shared/ui/atoms';
import { getAutocompleteSearchMessage } from '@/shared/utils/getAutocompleteSearchMessage';
import { useNotificationClassroomSearch } from '../../../hooks/useNotificationClassroomSearch';
import { useNotificationSenderSearch } from '../../../hooks/useNotificationSenderSearch';
import type {
  NotificationFilters as NotificationFiltersType,
  NotificationStatus,
} from '../../../types/notification';

export interface NotificationFiltersProps {
  readonly filters: NotificationFiltersType;
  readonly onFiltersChange: (partial: Partial<NotificationFiltersType>) => void;
  /** Indica que a lista está carregando (debounce pendente ou query em execução) */
  readonly isSearching?: boolean;
}

const STATUSES: NotificationStatus[] = [
  'delivered',
  'scheduled',
  'draft',
  'cancelled',
  'processing',
];

/**
 * Painel de filtros da listagem de notificações.
 *
 * Exibe campo de busca e botão que abre um popover com filtros avançados.
 * Filtros aplicados aparecem como chips removíveis abaixo da busca.
 *
 * @param filters - Estado atual dos filtros aplicados
 * @param onFiltersChange - Atualiza um ou mais filtros aplicados
 */
export function NotificationFilters({
  filters,
  onFiltersChange,
  isSearching = false,
}: NotificationFiltersProps) {
  const { t } = useTranslation('notification');
  const { t: tCommon } = useTranslation();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<NotificationFiltersType>(filters);

  const [sentBySearch, setSentBySearch] = useState('');
  const [classroomSearch, setClassroomSearch] = useState('');
  const [selectedSender, setSelectedSender] = useState<Item<string> | undefined>(undefined);
  const [selectedClassroom, setSelectedClassroom] = useState<Item<string> | undefined>(undefined);
  const [autocompleteKey, setAutocompleteKey] = useState(0);
  const [datePickerKey, setDatePickerKey] = useState(0);

  const { items: senderItems, isQuerySuccess: isSenderSuccess } = useNotificationSenderSearch(
    sentBySearch,
    open,
  );
  const { items: classroomItems, isQuerySuccess: isClassroomSuccess } =
    useNotificationClassroomSearch(classroomSearch, open);

  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen) {
      setDraft(filters);
      setSelectedSender(
        filters.sentBy ? { label: filters.sentByName, value: filters.sentBy } : undefined,
      );
      setSelectedClassroom(
        filters.classroomId
          ? { label: filters.classroomName, value: filters.classroomId }
          : undefined,
      );
      setSentBySearch('');
      setClassroomSearch('');
    }
    setOpen(nextOpen);
  };

  const updateDraft = (partial: Partial<NotificationFiltersType>) => {
    setDraft(prev => ({ ...prev, ...partial }));
  };

  const toggleDraftStatus = (status: NotificationStatus) => {
    const current = draft.statuses as NotificationStatus[];
    const next = current.includes(status)
      ? current.filter(s => s !== status)
      : [...current, status];
    updateDraft({ statuses: next });
  };

  const handleApply = () => {
    onFiltersChange(draft);
    setOpen(false);
  };

  const handleClear = () => {
    setDraft(prev => ({
      ...prev,
      statuses: [],
      startDate: '',
      endDate: '',
      sentBy: '',
      sentByName: '',
      classroomId: '',
      classroomName: '',
    }));
    setSelectedSender(undefined);
    setSelectedClassroom(undefined);
    setSentBySearch('');
    setClassroomSearch('');
    setAutocompleteKey(k => k + 1);
    setDatePickerKey(k => k + 1);
  };

  const removeStatus = (status: NotificationStatus) => {
    onFiltersChange({ statuses: filters.statuses.filter(s => s !== status) });
  };

  const activeFilterCount =
    filters.statuses.length +
    (filters.startDate ? 1 : 0) +
    (filters.endDate ? 1 : 0) +
    (filters.sentBy ? 1 : 0) +
    (filters.classroomId ? 1 : 0);

  const senderNotFoundMessage = getAutocompleteSearchMessage(
    sentBySearch.length,
    isSenderSuccess,
    senderItems.length,
    {
      minChars: tCommon('search.minChars'),
      searching: tCommon('search.searching'),
      notFound: tCommon('search.notFound'),
    },
  );

  const classroomNotFoundMessage = getAutocompleteSearchMessage(
    classroomSearch.length,
    isClassroomSuccess,
    classroomItems.length,
    {
      minChars: tCommon('search.minChars'),
      searching: tCommon('search.searching'),
      notFound: tCommon('search.notFound'),
    },
  );

  return (
    <div className="flex flex-col gap-nano">
      {/* Search + Filtros */}
      <div className="flex items-center gap-mini">
        <div className="flex-1">
          <TextField
            placeholder={t('filters.searchPlaceholder')}
            value={filters.search}
            fullWidth
            {...(isSearching
              ? {
                  trailingIconSlot: (
                    <ArrowPathIcon className="h-xxxs w-xxxs animate-spin text-ctx-content-placeholder" />
                  ),
                }
              : { trailingIcon: MagnifyingGlassIcon })}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              onFiltersChange({ search: e.target.value })
            }
            size="md"
          />
        </div>

        <div className="relative">
          <Popover.Root open={open} onOpenChange={handleOpenChange}>
            <Popover.Trigger asChild>
              <Button hierarchy="secondary" size="md" leadingIcon={FunnelIcon}>
                {t('filters.filtersButton')}
              </Button>
            </Popover.Trigger>

            <Popover.Portal>
              <Popover.Content
                align="end"
                sideOffset={8}
                className="z-50 w-[40rem] rounded-md border-hairline border-ctx-layout-border bg-ctx-layout-surface p-xs shadow-level3 focus:outline-none"
              >
                <div className="flex flex-col gap-xs">
                  <p className="ts-heading-sm text-ctx-content-title">{t('filters.panelTitle')}</p>

                  {/* Status da notificação */}
                  <div className="flex flex-col gap-micro">
                    <p className="ts-subtitle-xxs text-ctx-content-title">
                      {t('filters.statusLabel')}
                    </p>
                    <div className="flex flex-wrap gap-nano">
                      {STATUSES.map(status => (
                        <TagSelect
                          key={status}
                          label={t(`status.${status}` as never)}
                          size="md"
                          mode="filter"
                          active={draft.statuses.includes(status)}
                          onClick={() => toggleDraftStatus(status)}
                        />
                      ))}
                    </div>
                  </div>

                  {/* Datas */}
                  <div className="grid grid-cols-2 gap-mini">
                    <DatePicker
                      key={`start-${datePickerKey}`}
                      label={t('filters.startDate')}
                      placeholder={t('filters.startDatePlaceholder')}
                      value={draft.startDate}
                      onChange={iso => updateDraft({ startDate: iso })}
                      size="md"
                    />
                    <DatePicker
                      key={`end-${datePickerKey}`}
                      label={t('filters.endDate')}
                      placeholder={t('filters.endDatePlaceholder')}
                      value={draft.endDate}
                      onChange={iso => updateDraft({ endDate: iso })}
                      size="md"
                    />
                  </div>

                  {/* Enviado por + Turmas */}
                  <div className="grid grid-cols-2 gap-mini">
                    <Autocomplete<string>
                      key={`sender-${autocompleteKey}`}
                      label={t('filters.sentBy')}
                      placeholder={t('filters.sentByPlaceholder')}
                      list={senderItems}
                      custom={{ optionList: 'z-[100]', option: '!h-auto py-nano' }}
                      {...(senderNotFoundMessage !== undefined
                        ? { notFoundMessage: senderNotFoundMessage }
                        : {})}
                      {...(selectedSender !== undefined ? { selectedItem: selectedSender } : {})}
                      handleChange={(item: Item<string> | null) => {
                        const cleared = !item?.value;
                        setSelectedSender(cleared ? undefined : (item as Item<string>));
                        if (cleared) setSentBySearch('');
                        updateDraft({ sentBy: item?.value ?? '', sentByName: item?.label ?? '' });
                      }}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setSentBySearch(e.target.value)
                      }
                      size="md"
                    />

                    <Autocomplete<string>
                      key={`classroom-${autocompleteKey}`}
                      label={t('filters.classroomsLabel')}
                      placeholder={t('filters.classroomsPlaceholder')}
                      list={classroomItems}
                      custom={{ optionList: 'z-[100]', option: '!h-auto py-nano' }}
                      {...(classroomNotFoundMessage !== undefined
                        ? { notFoundMessage: classroomNotFoundMessage }
                        : {})}
                      {...(selectedClassroom !== undefined
                        ? { selectedItem: selectedClassroom }
                        : {})}
                      handleChange={(item: Item<string> | null) => {
                        const cleared = !item?.value;
                        setSelectedClassroom(cleared ? undefined : (item as Item<string>));
                        if (cleared) setClassroomSearch('');
                        updateDraft({
                          classroomId: item?.value ?? '',
                          classroomName: item?.label ?? '',
                        });
                      }}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setClassroomSearch(e.target.value)
                      }
                      size="md"
                    />
                  </div>

                  {/* Ações */}
                  <div className="flex items-center justify-between border-t-hairline border-ctx-layout-border pt-nano">
                    <Button hierarchy="tertiary" size="md" onClick={handleClear}>
                      {t('filters.clearFilters')}
                    </Button>
                    <Button hierarchy="primary" size="md" onClick={handleApply}>
                      {t('filters.applyFilters')}
                    </Button>
                  </div>
                </div>
              </Popover.Content>
            </Popover.Portal>
          </Popover.Root>

          {activeFilterCount > 0 && (
            <span className="pointer-events-none absolute left-[2rem] top-nano z-10 h-[0.625rem] w-[0.625rem] rounded-pill bg-base-accent-500" />
          )}
        </div>
      </div>

      {/* Filtros aplicados */}
      {filters.statuses.length > 0 && (
        <div className="flex flex-wrap items-center gap-nano">
          <span className="ts-subtitle-xxs text-ctx-content-title">
            {t('filters.appliedLabel')}
          </span>
          {(filters.statuses as NotificationStatus[]).map(status => (
            <TagSelect
              key={status}
              label={t(`status.${status}` as never)}
              size="sm"
              mode="removable"
              active
              onClickRemovable={() => removeStatus(status)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
